import {  t  } from "@/client/libs/dummy-i18n";
import { InfoIcon } from "@phosphor-icons/react";
import { Alert, AlertDescription, AlertTitle } from "@stackmorph/ui";
import { cn } from "@stackmorph/utils";
import { AnimatePresence, motion } from "framer-motion";

import { useResumeStatistics } from "@/client/services/resume";
import { useResumeStore } from "@/client/stores/resume";

import { SectionIcon } from "../shared/section-icon";

export const StatisticsSection = () => {
  const id = useResumeStore((state) => state.resume.id);
  const isPublic = useResumeStore((state) => state.resume.visibility === "public");

  const { statistics } = useResumeStatistics(id, isPublic);

  // Helper to render a simple list for sources/geography
  const renderList = (data: Record<string, number> | undefined, title: string) => {
    if (!data || Object.keys(data).length === 0) return null;
    
    const sorted = Object.entries(data).sort(([, a], [, b]) => b - a);

    return (
      <div className="col-span-2 grid gap-y-2">
        <h4 className="font-semibold text-sm opacity-75">{title}</h4>
        <div className="grid gap-y-1">
          {sorted.map(([key, value]) => (
            <div key={key} className="flex items-center justify-between text-xs">
              <span>{key}</span>
              <span className="font-medium">{value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <section id="statistics" className="grid gap-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          <SectionIcon id="statistics" size={18} name={t`Statistics`} />
          <h2 className="line-clamp-1 text-2xl font-bold lg:text-3xl">{t`Statistics`}</h2>
        </div>
      </header>

      <main className="grid grid-cols-2 gap-x-4 gap-y-6">
        <AnimatePresence>
          {!isPublic && (
            <motion.div
              className="col-span-2"
              initial={{ opacity: 0, y: -50, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -50, filter: "blur(10px)" }}
            >
              <Alert variant="info">
                <InfoIcon size={18} />
                <AlertTitle>{t`Statistics are available only for public resumes.`}</AlertTitle>
                <AlertDescription className="text-xs leading-relaxed">
                  {t`You can track the number of views your resume has received, or how many people have downloaded the resume by enabling public sharing.`}
                </AlertDescription>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        <div>
          <h3 className={cn("text-4xl font-bold blur-none transition-all", !isPublic && "blur-sm")}>
            {statistics?.viewCount ?? 0}
          </h3>
          <p className="opacity-75">{t`Views`}</p>
        </div>

        <div>
          <h3 className={cn("text-4xl font-bold blur-none transition-all", !isPublic && "blur-sm")}>
            {statistics?.downloadCount ?? 0}
          </h3>
          <p className="opacity-75">{t`Downloads`}</p>
        </div>

        {isPublic && (
          <>
            {renderList(statistics?.sources, t`Traffic Sources`)}
            {renderList(statistics?.geography, t`Geography`)}
          </>
        )}
      </main>
    </section>
  );
};
